import { useState } from "react";

import { NodeRevokeError, revokeNode } from "./api";

type RevokeState =
  | { readonly status: "idle" }
  | { readonly status: "revoking" }
  | { readonly status: "failed"; readonly message: string };

/** Explain a refused revocation without exposing a response body. */
function revokeFailureMessage(error: unknown): string {
  if (!(error instanceof NodeRevokeError)) {
    return "The identity could not be revoked. Try again.";
  }
  switch (error.reason) {
    case "session":
      return "Your administrator session has expired (it lasts 15 minutes). Reload the page, enter your authenticator code, then revoke again.";
    case "active-guest":
      return "This identity was not revoked: revoke its enrolled guest identities first, or it was already revoked.";
    case "service":
      return "Fleet could not revoke the identity. Nothing was changed; try again shortly.";
    default: {
      const unreachable: never = error.reason;
      return unreachable;
    }
  }
}

/**
 * Revoke one node identity after the operator types its exact name.
 *
 * Revocation is permanent for that key: the node stops being accepted and must run
 * `ha-fleet-agent reenroll` to report again. The typed name guards against revoking the wrong
 * node from a long inventory; the Worker still enforces the session, CSRF and origin checks.
 */
export function RevokeIdentity(
  { nodeId, nodeName, csrf, onRevoked }: {
    readonly nodeId: string;
    readonly nodeName: string;
    readonly csrf: string;
    readonly onRevoked: (name: string) => Promise<void>;
  },
) {
  const [typed, setTyped] = useState("");
  const [state, setState] = useState<RevokeState>({ status: "idle" });
  const confirmed = typed === nodeName;
  const revoke = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!confirmed) return;
    setState({ status: "revoking" });
    try {
      await revokeNode(nodeId, csrf);
    } catch (error: unknown) {
      setState({ status: "failed", message: revokeFailureMessage(error) });
      return;
    }
    await onRevoked(nodeName);
  };
  return (
    <section className="revoke-identity" aria-labelledby={`revoke-${nodeId}`}>
      <h3 id={`revoke-${nodeId}`}>Revoke identity</h3>
      <p>
        Revoking stops Fleet accepting uploads signed by this node&apos;s key, cancels its scheduled
        maintenance and disables its node alert rules. The machine itself is not changed. To report
        again it must run <code>ha-fleet-agent reenroll</code> and be approved as a new identity.
      </p>
      <form onSubmit={(event) => void revoke(event)}>
        <label>
          Type <strong>{nodeName}</strong> to confirm
          <input
            name="confirm-name"
            autoComplete="off"
            spellCheck={false}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </label>
        <button
          type="submit"
          className="danger-button"
          disabled={!confirmed || state.status === "revoking"}
        >
          {state.status === "revoking" ? "Revoking…" : "Revoke identity"}
        </button>
        {state.status === "failed"
          ? <p role="alert" className="error">{state.message}</p>
          : null}
      </form>
    </section>
  );
}
